/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { useRedirectToSubscribe } from "@packages/kids";
import { usePlayer } from "@packages/media";
import type { RadioStationDetails } from "@packages/sdk";
import {
  useRedirectToAuth,
  useRequestRadioStation,
  useSession,
} from "@packages/sdk";
import {
  cssMediaQueryMinWidthTablet,
  List,
  numericPercentages,
  numericPixels,
  spacing,
  Text,
  TextWithLinks,
  useIsDesktopViewport,
} from "@packages/ui";
import { GuideInfo } from "@ui/media";
import React, { Suspense } from "react";

import { RadioStationImageHeader } from "../../../../components";

const columnLeftStyles: Interpolation = {
  bottom: "0px",
  maxHeight: "calc(100vh - 80px)",
  maxWidth: "100vw",
  minWidth: "256px",
  overflowY: "auto",
  top: "80px",
  paddingRight: spacing.none,
  position: "inherit",
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    paddingRight: spacing.l,
    position: "sticky",
    width: `calc(${numericPercentages[40]} + ${spacing.l})`,
  },
};

const columnRightStyles: Interpolation = {
  flex: "1",
  paddingBottom: numericPixels[48],
};

const descriptionStyles: Interpolation = {
  height: "fit-content",
  marginTop: spacing.m,
  whiteSpace: "normal",
  margin: `${spacing.s} ${spacing.l}`,
  [cssMediaQueryMinWidthTablet]: {
    margin: `${spacing.s} ${spacing.none}`,
  },
};

const detailsContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "column-reverse",
  },
};

const pageStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  margin: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    margin: `${spacing.xl} ${spacing.xxl}`,
  },
};

const subtitleStyles: Interpolation = {
  display: "block",
  padding: `${spacing.m} ${spacing.l}`,
};

const listStyles: Interpolation = {
  padding: `${spacing.s} ${spacing.l}`,
  rowGap: spacing.l,
};

type RadioStationClientProps = {
  id: number;
  radioStation: RadioStationDetails;
};

export const RadioStationClient = ({
  id,
  radioStation,
}: RadioStationClientProps) => {
  const { query } = useRequestRadioStation({ id, initialData: radioStation });
  const isDesktop = useIsDesktopViewport();
  const player = usePlayer();
  const analytics = useAnalytics();
  const t = useTranslations();
  const { status } = useSession();
  const redirectToSubscribe = useRedirectToSubscribe();
  const redirectToAuth = useRedirectToAuth();

  useViewedScreen({ screen: "radio_station_detail" });

  const playStation = async () => {
    if (query?.data?.radio_station?.has_access) {
      analytics.track({
        event: HallowAnalyticsEvent.TappedPlayRadioStation,
        properties: {
          radio_station_id: id,
        },
      });
      await player?.playNew({ id, type: "radio_station" });
    } else {
      analytics.track({
        event: HallowAnalyticsEvent.TappedSubscribeToListen,
        properties: {
          screen_name: "radio_station_detail",
          radio_station_id: id,
        },
      });
      return status !== "authenticated"
        ? redirectToAuth()
        : redirectToSubscribe({
            redirectBackOnSubscription: true,
            forContent: {
              title: radioStation.radio_station.name,
              images: radioStation.radio_station.images,
            },
          });
    }
  };

  return (
    <Suspense>
      <div css={pageStyles}>
        <div css={columnLeftStyles}>
          <RadioStationImageHeader
            radioStation={query.data?.radio_station}
            playCTA={playStation}
          />
          <div css={detailsContainerStyles}>
            {isDesktop ? (
              <TextWithLinks size="m" css={descriptionStyles} type="body">
                {query.data?.radio_station.desc}
              </TextWithLinks>
            ) : (
              <TextWithLinks
                expandable={true}
                lineClamp={3}
                overflow={"ellipsis"}
                size="m"
                css={descriptionStyles}
                type="body"
              >
                {query.data?.radio_station.desc}
              </TextWithLinks>
            )}
          </div>
        </div>
        <div css={columnRightStyles}>
          <Text size="m" type="title" css={subtitleStyles}>
            {t("radio_station_featuring")}
          </Text>
          <List css={listStyles}>
            {query.data?.artists.map((artist) => (
              <GuideInfo key={artist.name} guide={artist} />
            ))}
          </List>
        </div>
      </div>
    </Suspense>
  );
};
